import { useCallback, useMemo } from 'react';
import {
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
  type UrlKeys,
} from 'nuqs';
import { IVolTab } from 'types/impliedVol';
import { freeSymbolState } from 'states';
import { useRecoilValue } from 'recoil';
import { ProductType } from 'types';
import useAuth from 'hooks/auth/useAuth';

const DEFAULT_SYM = 'SPX';

export interface IVolPageConfig {
  sym: string;
  tab: IVolTab;
}

// Shared config for implied volatility page
export function useImpliedVolatilityConfig(): [
  IVolPageConfig,
  (patch: Partial<IVolPageConfig>) => void,
] {
  const { hasAccessToProduct } = useAuth();
  const hasIVolAccess = hasAccessToProduct(ProductType.IMPLIED_VOL);
  const freeSym = useRecoilValue(freeSymbolState);
  const IVOL_PAGE_PARSERS = {
    sym: parseAsString.withDefault(
      hasIVolAccess ? DEFAULT_SYM : (freeSym ?? DEFAULT_SYM),
    ),
    tab: parseAsStringEnum<IVolTab>(Object.values(IVolTab)).withDefault(
      IVolTab.FixedStrikeMatrix,
    ),
  } as const;

  const urlKeys: UrlKeys<typeof IVOL_PAGE_PARSERS> = useMemo(
    () => ({
      sym: 'ivol_sym',
      tab: 'ivol_tab',
    }),
    [],
  );

  const [params, setParams] = useQueryStates(IVOL_PAGE_PARSERS, { urlKeys });

  const config: IVolPageConfig = {
    sym: params.sym,
    tab: params.tab,
  };

  const updateConfig = useCallback(
    (patch: Partial<IVolPageConfig>) => {
      const urlPatch: Partial<{ sym: string; tab: IVolTab }> = {};
      if (patch.sym != null) {
        urlPatch.sym = patch.sym;
      }
      if (patch.tab != null) {
        urlPatch.tab = patch.tab;
      }
      void setParams(urlPatch);
    },
    [setParams],
  );

  return [config, updateConfig];
}
